import { Nft } from '@/types'

import { safeBigInt } from './atomic'

type NftVesting = Pick<
  Nft,
  | 'reward_per_period'
  | 'token_amount'
  | 'unlocked_amount'
  | 'withdrawn_amount'
  | 'start_vesting_block'
  | 'last_vesting_block'
>

export function isNftFullyUnlocked(nft: NftVesting): boolean {
  return (
    safeBigInt(nft.unlocked_amount?.amount) >=
    safeBigInt(nft.token_amount?.amount)
  )
}

export function getNftWithdrawableAtomic(
  nft: NftVesting,
  balanceAtomic?: string | null,
): string {
  const balance = balanceAtomic == null ? null : safeBigInt(balanceAtomic)

  if (isNftFullyUnlocked(nft) && balance !== null) return balance.toString()

  const unlocked =
    safeBigInt(nft.unlocked_amount?.amount) -
    safeBigInt(nft.withdrawn_amount?.amount)
  const limit = unlocked > 0n ? unlocked : 0n

  if (balance === null) return limit.toString()
  return (balance < limit ? balance : limit).toString()
}

export function getNftUnlockPercent(nft: NftVesting): number {
  const total = safeBigInt(nft.token_amount?.amount)
  if (total <= 0n) return 0

  const unlocked = safeBigInt(nft.unlocked_amount?.amount)
  if (unlocked >= total) return 100

  return Number((unlocked * 10_000n) / total) / 100
}

export function getNftRemainingPeriods(nft: NftVesting): number {
  const remainder =
    safeBigInt(nft.token_amount?.amount) -
    safeBigInt(nft.unlocked_amount?.amount)
  const step = safeBigInt(nft.reward_per_period?.amount)
  if (remainder <= 0n || step <= 0n) return 0

  return Number((remainder + step - 1n) / step)
}

export function getNftFullUnlockBlock(
  nft: NftVesting,
  vestingPeriodBlocks: number,
): number | null {
  if (isNftFullyUnlocked(nft)) return null
  if (!Number.isFinite(vestingPeriodBlocks) || vestingPeriodBlocks <= 0)
    return null

  const lastVestingBlock = Number(nft.last_vesting_block)
  const base =
    lastVestingBlock > 0
      ? lastVestingBlock
      : Number(nft.start_vesting_block) - vestingPeriodBlocks

  return base + getNftRemainingPeriods(nft) * vestingPeriodBlocks
}

export function getNftTopUpExtraPeriods(
  nft: NftVesting,
  topUpAtomic: string,
): number {
  const extended = {
    ...nft,
    token_amount: {
      ...nft.token_amount,
      amount: (
        safeBigInt(nft.token_amount?.amount) + safeBigInt(topUpAtomic)
      ).toString(),
    },
  }

  return getNftRemainingPeriods(extended) - getNftRemainingPeriods(nft)
}
